import { UserStatus, MessageLimits } from "../types";
import { ApiUrls, useAuthStore } from "@store"; // Import from barrel
import * as Sentry from "@sentry/react"; // Added Sentry
import { logDebug, logInfo, logWarn, logError } from "../utils/logging"; // Import logging utilities

// Define the expected structure of the response from the /status WordPress REST endpoint
interface WpStatusEndpointResponse {
  token?: string | null;
  user_status?: UserStatus; // Note: WP endpoint uses snake_case for user_status
  message_limits?: MessageLimits | null; // Updated: WP endpoint now sends this
  login_url?: string;
  upgrade_url?: string;
  backend_api_url?: string; // Added to receive from WP
}

export interface InitialDataResponse {
  jwt: string | null;
  userStatus: UserStatus;
  messageLimits: MessageLimits | null; // Keep for consistency, though not directly from this endpoint yet
  apiUrls: ApiUrls | null;
}

/**
 * Fetches a fresh JWT token from the development backend endpoint
 * BUGFIX F2.5.1: Use backend development endpoint for valid JWT tokens
 */
const getDevJWTFromBackend = async (): Promise<{
  jwt: string;
  userStatus: UserStatus;
  messageLimits: MessageLimits;
}> => {
  logDebug("Fetching development JWT token from backend");

  try {
    const response = await fetch("http://127.0.0.1:8000/api/v1/auth/dev-token");
    if (!response.ok) {
      throw new Error(`Failed to get dev token: ${response.status}`);
    }

    const data = await response.json();
    logDebug("Development JWT token received from backend");

    return {
      jwt: data.token,
      userStatus: data.user_status as UserStatus,
      messageLimits: data.message_limits,
    };
  } catch (error) {
    logError("Failed to fetch development JWT token from backend:", error);
    throw error;
  }
};

/**
 * Retrieves initial user data and JWT from the WordPress /status REST endpoint.
 */
export const getInitialWordPressData =
  async (): Promise<InitialDataResponse> => {
    const defaultResponse: InitialDataResponse = {
      jwt: null,
      userStatus: "guest", // Default to guest if nothing is found
      messageLimits: null,
      apiUrls: {
        backendBaseUrl: (
          window as unknown as {
            smartieAiData?: { backendApiBaseUrl?: string };
          }
        ).smartieAiData?.backendApiBaseUrl,
      },
    };

    try {
      // BUGFIX F2.5.1: For localhost development, fetch a fresh JWT token from backend
      if (
        window.location.hostname === "localhost" ||
        window.location.hostname === "127.0.0.1"
      ) {
        logInfo(
          "Smartie AI: Local development detected, fetching fresh JWT token from backend",
        );
        try {
          const devAuth = await getDevJWTFromBackend();
          return {
            jwt: devAuth.jwt,
            userStatus: devAuth.userStatus,
            messageLimits: devAuth.messageLimits,
            apiUrls: {
              backendBaseUrl: "http://127.0.0.1:8000/api/v1",
            },
          };
        } catch (error) {
          logError(
            "Failed to get development JWT, falling back to WordPress endpoint",
            error,
          );
          // Fall through to normal WordPress endpoint logic
        }
      }

      // The frontend is served from WordPress, so relative path should work.
      const response = await fetch("/wp-json/smarters-ai/v1/status", {
        credentials: "include", // Ensure WordPress auth cookies are sent
      });
      if (!response.ok) {
        const errorContext = {
          status: response.status,
          statusText: response.statusText,
          url: response.url,
        };
        logError(
          "Smartie AI: Failed to fetch initial data from /status endpoint",
          errorContext,
        );
        if (import.meta.env.PROD) {
          Sentry.captureMessage(
            "Failed to fetch initial data from /status endpoint",
            {
              contexts: { fetch_error: errorContext },
              level: "warning", // Or "error" depending on severity
            },
          );
        }
        if (response.status === 404) {
          const error = new Error(
            "Initial data endpoint not found (404). Check plugin activation and URL.",
          );
          if (import.meta.env.PROD) {
            Sentry.captureException(error, { extra: errorContext });
          }
          throw error;
        }
        return defaultResponse;
      }

      const data: WpStatusEndpointResponse = await response.json();
      logInfo("Smartie AI: Initial data received from /status endpoint:", data);

      return {
        jwt: data.token !== undefined ? data.token : defaultResponse.jwt,
        userStatus:
          data.user_status !== undefined
            ? data.user_status
            : defaultResponse.userStatus,
        messageLimits:
          data.message_limits !== undefined
            ? data.message_limits
            : defaultResponse.messageLimits,
        apiUrls: {
          loginUrl: data.login_url !== undefined ? data.login_url : undefined,
          upgradeUrl:
            data.upgrade_url !== undefined ? data.upgrade_url : undefined,
          backendBaseUrl:
            data.backend_api_url !== undefined
              ? data.backend_api_url
              : defaultResponse.apiUrls?.backendBaseUrl, // Store backend_api_url
        },
      };
    } catch (error) {
      logError(
        "Smartie AI: Error fetching or parsing initial WordPress data:",
        error,
      );
      if (import.meta.env.PROD) {
        Sentry.captureException(error, {
          // Added Sentry
          tags: { service: "authService", method: "getInitialWordPressData" },
        });
      }
      return defaultResponse;
    }
  };

/**
 * Fetches the latest user usage data (message limits) from the WordPress /status endpoint
 * and updates the authStore.
 */
export const refreshCurrentUserUsage = async (): Promise<void> => {
  const store = useAuthStore.getState();
  logDebug(
    "[AuthService] refreshCurrentUserUsage: Called. Current store userStatus:",
    store.userStatus,
    "current messageLimits:",
    JSON.stringify(store.messageLimits),
  );

  try {
    const response = await fetch("/wp-json/smarters-ai/v1/status", {
      credentials: "include",
    });

    if (!response.ok) {
      const errorContext = {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
      };
      logError(
        "Smartie AI: Failed to refresh user usage data from /status endpoint",
        errorContext,
      );
      if (import.meta.env.PROD) {
        Sentry.captureMessage(
          "Failed to refresh user usage data from /status endpoint",
          {
            contexts: { fetch_error: errorContext },
            level: "warning",
          },
        );
      }
      return;
    }

    const data: WpStatusEndpointResponse = await response.json();
    logDebug(
      "[AuthService] User usage data refreshed from /status endpoint:",
      JSON.stringify(data),
    );

    if (data.message_limits) {
      const newLimits = data.message_limits;
      logDebug(
        "[AuthService] refreshCurrentUserUsage: Received newLimits:",
        JSON.stringify(newLimits),
        "for userStatus from data:",
        data.user_status,
      );

      store.setInitialData({
        messageLimits: newLimits,
        jwt: data.token, // Update JWT to keep session alive and prevent expiration
      });
      logDebug(
        "[AuthService] refreshCurrentUserUsage: Store updated with newLimits and JWT. New store messageLimits:",
        JSON.stringify(useAuthStore.getState().messageLimits),
      );

      if (
        newLimits.current !== null &&
        newLimits.max !== null &&
        newLimits.current >= newLimits.max
      ) {
        logInfo(
          "[AuthService] refreshCurrentUserUsage: Rate limit reached. newLimits.current:",
          newLimits.current,
          "newLimits.max:",
          newLimits.max,
        );
        store.setRateLimitError(true, "Limita de mesaje a fost atinsă.");
      } else {
        if (
          store.isRateLimited &&
          store.apiError === "Limita de mesaje a fost atinsă."
        ) {
          logInfo(
            "[AuthService] refreshCurrentUserUsage: Clearing rate limit error.",
          );
          store.setRateLimitError(false);
        }
      }
    } else {
      logWarn(
        "Smartie AI: Refreshed data from /status did not contain message_limits.",
      );
    }
  } catch (error) {
    logError(
      "Smartie AI: Error fetching or parsing refreshed user usage data:",
      error,
    );
    if (import.meta.env.PROD) {
      Sentry.captureException(error, {
        // Added Sentry
        tags: { service: "authService", method: "refreshCurrentUserUsage" },
      });
    }
  }
};
